'use client';

import type { DetailHomeGiftProductResponse } from '__generated__/api/unauth';
import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { cn, isEdge } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { AuthQueryOptions, VERIFICATION_TYPE } from '@/api';
import {
  buildSingleOrderSearchParams,
  hasAllRequiredOptions,
} from '@/app/donation/_utils/singleOrder';
import {
  GIFT_SALE_STATUS,
  GIFT_SALE_STATUS_LABEL,
} from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

export interface CartOption {
  optionGroupId: number;
  optionGroupName: string;
  optionItemId: number;
  optionItemName: string;
  extraPrice: number;
}

interface AddToCartButtonProps {
  productInfo: DetailHomeGiftProductResponse;
  productNumber: string;
  /** 선택된 옵션 정보 (PrefillForm에서 전달) */
  selectedOptions?: CartOption[];
  /** 옵션 유무 */
  hasOptions?: boolean;
  /** 버튼 텍스트 커스텀 (옵션 선택 여부에 따라) */
  customButtonText?: string;
  className?: string;
  /** 버튼 dimensions */
  dimensions?: 'H56' | 'H48' | 'H40';
  /** 버튼 theme */
  theme?: 'primary-fill' | 'sub-line' | 'sub-fill';
  /** 필수 옵션 그룹 ID 목록 */
  requiredOptionGroupIds?: number[];
  /** 옵션 미선택 시 호출되는 콜백 (스크롤 등) */
  onOptionNotSelected?: () => void;
}

const SELECT_OPTION_MESSAGE = '구성품을 선택해주세요';

type ModalType = 'NONE' | 'EDGE' | 'LOGIN';

export const AddToCartButton = ({
  productInfo,
  productNumber,
  selectedOptions = [],
  hasOptions = false,
  customButtonText,
  className,
  dimensions = 'H56',
  theme = 'primary-fill',
  requiredOptionGroupIds = [],
  onOptionNotSelected,
}: AddToCartButtonProps) => {
  const router = useCustomRouter();
  const pathname = usePathname();
  const { toast } = useToast();
  const [modalType, setModalType] = useState<ModalType>('NONE');

  const isLogin = useAuthStore((state) => !!state.isLogin);
  const verificationInfoQuery = useQuery({
    ...AuthQueryOptions['post:/wello/v2/auth/member-verification/info'](),
    enabled: isLogin,
  });

  const isDisabled = productInfo.productSaleStatus !== GIFT_SALE_STATUS.ON_SALE;

  const getButtonText = () => {
    if (customButtonText) return customButtonText;

    if (isDisabled && productInfo.productSaleStatus) {
      return GIFT_SALE_STATUS_LABEL[
        productInfo.productSaleStatus as GiftSaleStatus
      ];
    }

    // 옵션이 있으면 "선택한 답례품 주문하기"
    if (hasOptions) {
      return '선택한 답례품 주문하기';
    }

    return '답례품 주문하기';
  };

  // 주문서로 직행 (단건 주문 — 장바구니 없음)
  const navigateToOrder = () => {
    router.push(
      DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildSingleOrderSearchParams({
          institutionId: productInfo.institutionId,
          institutionName: productInfo.institutionName,
          productNumber,
          optionItemIds: hasOptions
            ? selectedOptions.map((option) => option.optionItemId)
            : [],
        }),
      }),
    );
  };

  // 동의 완료 후 주문서로 이동
  const handleAddToCartAfterAgreement = () => {
    const isMissingRequiredOption =
      hasOptions &&
      !hasAllRequiredOptions(
        selectedOptions.map((option) => option.optionGroupId),
        requiredOptionGroupIds,
      );

    if (isMissingRequiredOption) {
      toast({
        message: SELECT_OPTION_MESSAGE,
        type: 'info',
        offset: { bottom: 80 },
      });
      onOptionNotSelected?.();

      return;
    }

    navigateToOrder();
  };

  // 버튼 클릭 핸들러
  const handleAddToCart = () => {
    // 0. Edge 브라우저 체크
    if (isEdge()) {
      setModalType('EDGE');

      return;
    }

    // 1. 로그인 체크
    if (!isLogin) {
      setModalType('LOGIN');

      return;
    }

    // 2. 본인인증 체크
    const isCi = !!verificationInfoQuery.data?.context?.ci;
    if (!isCi) {
      router.push(
        ROUTES.EASY_CERTIFICATION.withSearchParams({
          searchParams: {
            verification: VERIFICATION_TYPE.PROFILE_UPDATE,
            redirect: pathname ?? '',
          },
        }),
      );

      return;
    }

    handleAddToCartAfterAgreement();
  };

  return (
    <>
      <Button
        className={cn(
          className,
          isDisabled && 'border-Gray-100 bg-Gray-100 text-Gray-400',
        )}
        dimensions={dimensions}
        theme={theme}
        onClick={handleAddToCart}
        disabled={isDisabled}
        aria-label="답례품 주문하기"
      >
        {getButtonText()}
      </Button>

      {/* Edge 브라우저 경고 모달 */}
      <Modal opened={modalType === 'EDGE'} onClose={() => setModalType('NONE')}>
        <div className="flex flex-col gap-16 text-center">
          <h2 className="font-700 text-Gray-900 typo-body2">
            웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.
          </h2>
          <p className="font-400 text-Gray-700 typo-body3">
            원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.
          </p>
          <Button onClick={() => setModalType('NONE')}>확인</Button>
        </div>
      </Modal>

      {/* 로그인 모달 */}
      <Modal
        opened={modalType === 'LOGIN'}
        onClose={() => setModalType('NONE')}
      >
        <div className="flex flex-col gap-8">
          <h2 className="font-700 text-Gray-900 typo-body2">
            로그인이 필요해요
          </h2>
          <p className="text-center font-400 text-Gray-700 typo-body2">
            로그인 후 주문할 수 있어요
          </p>
          <div className="flex gap-8 pt-12">
            <Button
              className="flex-1"
              theme="sub-fill"
              onClick={() => setModalType('NONE')}
            >
              취소
            </Button>
            <Button asChild className="flex-1">
              <Link
                href={ROUTES.LOGIN.withSearchParams({
                  searchParams: { redirect: pathname ?? '' },
                })}
              >
                로그인
              </Link>
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
